import React, { Fragment } from 'react';
import { Clock } from 'lucide-react';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useLocation } from 'react-router-dom';
import CustomInbox from '@/components/notifications/CustomInbox';
import CustomOrganizationSwitcher from './CustomOrganizationSwitcher';

const INBOX_HEADER_STYLE = {
  unassigned: {
    bg: 'bg-gradient-to-r from-slate-50 via-white to-orange-50/30',
    iconBg: 'bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/25',
    badgeBg: 'bg-orange-100 border border-orange-200',
    badgeText: 'text-orange-700',
    badgeIcon: 'text-orange-600',
    dotDefault: 'bg-orange-500',
    statText: 'text-orange-700',
  },
  yourInbox: {
    bg: 'bg-gradient-to-r from-green-50 via-white to-emerald-50/30',
    iconBg: 'bg-gradient-to-br from-emerald-500 to-green-400 shadow-lg shadow-emerald-500/25',
    badgeBg: 'bg-emerald-100 border border-emerald-200',
    badgeText: 'text-emerald-700',
    badgeIcon: 'text-emerald-600',
    dotDefault: 'bg-emerald-500',
    statText: 'text-emerald-700',
  },
  mentions: {
    bg: 'bg-gradient-to-r from-blue-50 via-white to-purple-50/30',
    iconBg: 'bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg shadow-blue-500/25',
    badgeBg: 'bg-blue-100 border border-blue-200',
    badgeText: 'text-blue-700',
    badgeIcon: 'text-blue-600',
    dotDefault: 'bg-blue-500',
    statText: 'text-blue-700',
  },
  all: {
    bg: 'bg-gradient-to-r from-white via-blue-50/30 to-indigo-50/40',
    iconBg: 'bg-blue-500',
    badgeBg: 'bg-blue-100 border border-blue-200',
    badgeText: 'text-blue-700',
    badgeIcon: 'text-blue-600',
    dotDefault: 'bg-blue-500',
    statText: 'text-blue-700',
  },
};

function getInboxTypeFromPath(pathname: string) {
  if (pathname.includes('/inbox/unassigned')) return 'unassigned';
  if (pathname.includes('/inbox/your-inbox')) return 'yourInbox';
  if (pathname.includes('/inbox/mentions')) return 'mentions';
  if (pathname.includes('/inbox/all')) return 'all';
  if (pathname.includes('automation/workflows/edit/')) return null;
  return 'all';
}

/**
 * Dynamic header component that displays page title, description, and stats
 * Data is provided via PageHeaderContext from individual pages
 */
const DynamicHeader: React.FC = () => {
  const { headerData } = usePageHeader();
  const location = useLocation();

  if (!headerData) {
    return null;
  }

  const { title, description, icon: Icon, stats } = headerData;
  const isInboxPage = location.pathname.includes('/inbox');
  const inboxType = getInboxTypeFromPath(location.pathname);

  if (inboxType === null) {
    return null;
  }
  const style = INBOX_HEADER_STYLE[inboxType];

  return (
    <div className={`sticky top-0 z-20 flex-none ${style.bg} border-b border-gray-200/60`}>
      <div className="p-3 sm:p-4 lg:p-6">
        <div className="flex items-center justify-between">
          {/* Left Section - Title and Description */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 mr-3">
            {Icon && (
              <div className={`p-1.5 sm:p-2 lg:p-2.5 ${style.iconBg} rounded-lg sm:rounded-xl shadow-sm flex-shrink-0`}>
                <Icon className="h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 text-white" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 sm:gap-3 mb-0.5">
                <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 truncate">{title}</h1>
                {/* Show badge only on larger screens for inbox pages */}
                {stats && stats.length > 0 && isInboxPage && (
                  <div className={`hidden sm:flex items-center gap-1.5 px-2 py-0.5 sm:px-2.5 sm:py-1 ${style.badgeBg} rounded-full flex-shrink-0`}>
                    <Clock className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${style.badgeIcon}`} />
                    <span className={`text-xs font-medium ${style.badgeText}`}>
                      {stats.find(s => s.label.toLowerCase().includes('total'))?.value || stats[0]?.value} {stats.find(s => s.label.toLowerCase().includes('total'))?.label.toLowerCase() || title.toLowerCase()}
                    </span>
                  </div>
                )}
              </div>
              {description && (
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 truncate">{description}</p>
              )}
            </div>
          </div>

          {/* Right Section - Controls only */}
          <div className="flex items-center gap-1 sm:gap-2 lg:gap-4 flex-shrink-0">
            {/* Stats Section - Only show on large screens */}
            {stats && stats.length > 0 && isInboxPage && (
              <div className="hidden xl:block">
                <div className="flex items-center gap-4 lg:gap-6 px-3 lg:px-4 py-1.5 lg:py-2 bg-white/80 backdrop-blur-sm rounded-lg border border-gray-200/60 shadow-sm">
                  {stats.map((stat, index) => (
                    <div key={index} className="contents">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${stat.dotColor || style.dotDefault} flex-shrink-0`}
                        ></div>
                        <span className="text-sm text-gray-600 whitespace-nowrap">{stat.label}</span>
                        <span className={`text-lg font-semibold ${stat.color || style.statText} flex-shrink-0`}>
                          {stat.value}
                        </span>
                      </div>
                      {index < stats.length - 1 && (
                        <div className="w-px h-6 bg-gray-200"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Organization Switcher and Inbox Controls */}
            <div className="relative z-[100]">
              <CustomOrganizationSwitcher />
            </div>
            <div className="relative z-[100]">
              {import.meta.env.VITE_REACT_APP_COURIER_CLIENT_KEY && <CustomInbox showUnreadMessageCount={true} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicHeader;

